import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { UserCircle, Key, UsersThree, Trash, EnvelopeSimple, Globe, ArrowSquareOut } from "@phosphor-icons/react";

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState({ name: "", email: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "" });
  const [reset, setReset] = useState({ email: "", code: "", new_password: "", stage: "request" });

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", email: user.email || "" });
    loadUsers();
  }, [user]);

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data);
    } catch {}
  };

  const saveProfile = async () => {
    try {
      await api.put("/auth/profile", profile);
      toast.success("Profil güncellendi");
      await refresh();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const changePassword = async () => {
    if (pw.new_password !== pw.confirm) return toast.error("Şifreler eşleşmiyor");
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Şifre değiştirildi");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const addUser = async () => {
    try {
      await api.post("/auth/users", newUser);
      toast.success("Kullanıcı eklendi");
      setNewUser({ email: "", password: "", name: "" });
      loadUsers();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const removeUser = async (id) => {
    if (!confirm("Bu kullanıcıyı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success("Kullanıcı silindi"); loadUsers();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const requestReset = async () => {
    try {
      const { data } = await api.post("/auth/password-reset/request", { email: reset.email });
      toast.success(data.email_sent ? "Doğrulama kodu e-postanıza gönderildi" : "Kod oluşturuldu (Resend anahtarı eksik — dev modda backend log'unda görülür)");
      setReset({ ...reset, stage: "confirm" });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const confirmReset = async () => {
    try {
      await api.post("/auth/password-reset/confirm", { email: reset.email, code: reset.code, new_password: reset.new_password });
      toast.success("Şifreniz sıfırlandı");
      setReset({ email: "", code: "", new_password: "", stage: "request" });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Profil & Kullanıcı Yönetimi</h1>
        <p className="text-sm text-muted-foreground mt-1">Hesap bilgilerinizi ve sistem kullanıcılarını yönetin.</p>
      </div>

      <Card className="border-border">
        <CardHeader><CardTitle className="font-heading flex items-center gap-2"><UserCircle size={20} weight="duotone" /> Profil Bilgileri</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Ad Soyad</Label><Input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></div>
          <div><Label>E-posta</Label><Input data-testid="profile-email" type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} /></div>
          <div className="md:col-span-2"><Button onClick={saveProfile} data-testid="save-profile-btn">Profili Kaydet</Button></div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Key size={20} weight="duotone" /> Şifre Değiştir (Mevcut Şifre ile)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label>Mevcut Şifre</Label><Input data-testid="cur-pw" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></div>
          <div><Label>Yeni Şifre</Label><Input data-testid="new-pw" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></div>
          <div><Label>Yeni Şifre (Tekrar)</Label><Input data-testid="confirm-pw" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} /></div>
          <div className="md:col-span-3"><Button onClick={changePassword} data-testid="change-pw-btn">Şifreyi Değiştir</Button></div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader><CardTitle className="font-heading flex items-center gap-2"><EnvelopeSimple size={20} weight="duotone" /> E-posta ile Şifre Sıfırlama</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">Kullanıcı e-postasına 6 haneli doğrulama kodu gönderir (Resend anahtarı gerekli, aksi halde kod backend log'una yazılır).</p>
          {reset.stage === "request" ? (
            <div className="flex gap-2 items-end">
              <div className="flex-1"><Label>E-posta</Label><Input data-testid="reset-email" type="email" value={reset.email} onChange={(e) => setReset({ ...reset, email: e.target.value })} /></div>
              <Button onClick={requestReset} data-testid="reset-request-btn">Kod Gönder</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>Doğrulama Kodu</Label><Input data-testid="reset-code" value={reset.code} onChange={(e) => setReset({ ...reset, code: e.target.value })} /></div>
              <div><Label>Yeni Şifre</Label><Input data-testid="reset-new-pw" type="password" value={reset.new_password} onChange={(e) => setReset({ ...reset, new_password: e.target.value })} /></div>
              <div className="flex items-end gap-2">
                <Button onClick={confirmReset} data-testid="reset-confirm-btn">Onayla</Button>
                <Button variant="outline" onClick={() => setReset({ email: "", code: "", new_password: "", stage: "request" })}>İptal</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Globe size={20} weight="duotone" /> Özel Domain (Custom Domain)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Mevcut URL</Label>
              <Input readOnly value={window.location.origin} data-testid="current-domain" className="bg-secondary/40" />
            </div>
            <div>
              <Label>Bağlamak İstediğiniz Domain</Label>
              <Input placeholder="nakit.cozummuhendislik.com.tr" data-testid="desired-domain" />
              <p className="text-xs text-muted-foreground mt-1">Bu alan bilgi amaçlıdır — bağlama Emergent panelinden yapılır.</p>
            </div>
          </div>
          <div className="bg-secondary/30 border border-border rounded-md p-4 text-sm space-y-2">
            <div className="font-semibold">Domain Bağlama Adımları:</div>
            <ol className="list-decimal ml-5 space-y-1 text-muted-foreground">
              <li>Emergent panelinizde bu uygulamayı açın → <b>"Link domain"</b> butonuna tıklayın</li>
              <li>Bağlamak istediğiniz alan adını girin (ör. <code className="bg-secondary px-1 rounded">nakit.cozummuhendislik.com.tr</code>)</li>
              <li>Emergent size bir <b>CNAME</b> kaydı gösterecek — bunu domain sağlayıcınızın DNS panelinde ekleyin</li>
              <li>Varsa eski çakışan <b>A record</b>'ları silin</li>
              <li>DNS yayılımı için <b>5-15 dakika</b> bekleyin (max 24 saat)</li>
              <li>Sorun olursa: <a href="mailto:destek@emergent.sh" className="text-primary underline">destek@emergent.sh</a></li>
            </ol>
            <div className="pt-2">
              <a href="https://app.emergent.sh" target="_blank" rel="noopener noreferrer" data-testid="open-emergent-panel">
                <Button variant="outline" size="sm"><ArrowSquareOut size={14} className="mr-2" /> Emergent Paneli Aç</Button>
              </a>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
            <b>Not:</b> Domain bağlama uygulama içinden yapılamaz — DNS kayıtları ve Emergent altyapı yönetimi gerektirir. Bu form tamamlandıktan sonra {" "}
            <a href="mailto:destek@emergent.sh?subject=Domain%20Ba%C4%9Flama%20Talebi" className="underline">destek@emergent.sh</a> adresine yazarsanız hızlıca yardımcı olurlar.
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader><CardTitle className="font-heading flex items-center gap-2"><UsersThree size={20} weight="duotone" /> Kullanıcılar</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div><Label>Ad</Label><Input data-testid="new-user-name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} /></div>
            <div><Label>E-posta</Label><Input data-testid="new-user-email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} /></div>
            <div><Label>Şifre</Label><Input data-testid="new-user-pw" type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} /></div>
            <div className="flex items-end"><Button onClick={addUser} data-testid="add-user-btn" className="w-full">Kullanıcı Ekle</Button></div>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Ad</TableHead><TableHead>E-posta</TableHead><TableHead>Oluşturuldu</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString("tr-TR") : "-"}</TableCell>
                  <TableCell className="text-right">
                    {u.id !== user?.id && (
                      <Button size="sm" variant="ghost" onClick={() => removeUser(u.id)}><Trash size={15} className="text-destructive" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
